import React, { useEffect, useState } from 'react';
import DrawerBackdropLoader from 'components/Molecules/Loaders/DrawerBackdropLoader/DrawerBackdropLoader';
import { FormikProps } from 'formik';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useAuthorization from 'hooks/useAuthorization';
import useLog from 'hooks/useLog';
import { UserRole } from 'models/user';
import { UseMutationResult, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import PaymentsService from 'services/Payments/payments.service';
import { TStore } from 'store';
import { formatNumber, getCurrencySymbol } from 'utils/currency.helpers';
import t from 'utils/translationHelper';

import { Approver } from '@alpha/auth-dtos';
import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import {
  DrawdownFundingMethod,
  DrawdownSubmissionRequest, DrawdownValidationDto, MassDrawdownValidationDto, TradeDto,
} from '@alpha/fx-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button/ActionButton';
import { BaseDrawer } from '@alpha/ui-lib/ui/Drawer/APBaseDrawer';
import {
  Box,
} from '@alpha/ui-lib/ui/external';
import { ConfirmationModal } from '@alpha/ui-lib/ui/Modal/ConfirmationModal';
import { StyledKeyValueTable, TKeyValue } from '@alpha/ui-lib/ui/table';
import {
  faArrowDown,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TAuthyState } from '../../../../store/authy/reducer';

import DrawdownConfirmation from './DrawdownConfirmation';
import DrawdownCreation from './DrawdownCreation';
import { FormikType, MassDrawdownRequestForm } from './formData';
import useStyles from './NewMassDrawdownDrawer.styles';
import Settlement from './Settlement';

/* eslint max-lines-per-function: off */

export type Section = 'Drawdown' | 'Settlement' | 'Submit';

type ConfirmationModalType = 'CLEAR' | 'CLOSE' | 'REMOVE' | 'BACK_TO_DRAWDOWN';

interface INewMassDrawdownDrawer {
  massDrawdownDrawerOpen: boolean;
  selectedTrades: TradeDto[];
  requestForm: FormikProps<MassDrawdownRequestForm>;
  handleSplitChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleCurrencyChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleAmountChange: (
    amount: number,
    trade: TradeDto
  ) => void;
  handleFormSubmit: (tradesToDrawdown: TradeDto[]) => void;
  massDrawdownValueDate: string | null;
  additionalTrades: TradeDto[];
  handleAddAdditionalTrade: (tradeId: string) => void;
  paymentsForm: FormikProps<FormikType>;
  confirmationModalType: ConfirmationModalType;
  setConfirmationModalType: (type: ConfirmationModalType) => void;
  openConfirmation: boolean;
  setOpenConfirmation: (open: boolean) => void;
  handleConfirmationModal: () => void;
  setTradeIdToRemove: (tradeId: string) => void;
  validateDrawdownMutations: UseMutationResult<DrawdownValidationDto, Error, DrawdownValidationDto, unknown>;
  section: Section;
  handleSetSection: (section: Section) => void;
  defaultBuyCurrencyAccount: CurrencyAccountDto;
  defaultSellCurrencyAccount: CurrencyAccountDto;
  accountConfigQuery: UseQueryResult<any, Error>;
  validatedDrawdownsResponse?: MassDrawdownValidationDto;
  calculatedWeightedRate: number;
  tradeErrors: Record<string, string>;
  handleDrawdownMFA: () => void;
  submitDrawdownMutations: UseMutationResult<any, Error, any, unknown>;
  drawdownSubmissionForm: FormikProps<DrawdownSubmissionRequest>;
}

const NewMassDrawdownDrawer: React.FC<INewMassDrawdownDrawer> = ({
  massDrawdownDrawerOpen,
  selectedTrades,
  requestForm,
  handleSplitChange,
  handleCurrencyChange,
  handleAmountChange,
  massDrawdownValueDate,
  additionalTrades,
  handleAddAdditionalTrade,
  paymentsForm,
  confirmationModalType,
  setConfirmationModalType,
  openConfirmation,
  setOpenConfirmation,
  handleConfirmationModal,
  setTradeIdToRemove,
  handleFormSubmit,
  validateDrawdownMutations,
  section,
  handleSetSection,
  defaultBuyCurrencyAccount,
  defaultSellCurrencyAccount,
  accountConfigQuery,
  validatedDrawdownsResponse,
  calculatedWeightedRate,
  tradeErrors,
  handleDrawdownMFA,
  submitDrawdownMutations,
  drawdownSubmissionForm,
}) => {
  const classes = useStyles();
  const sb = useAlphaSnackbar();

  const authyState = useSelector<TStore, TAuthyState>((state) => state.authy);
  const { logError } = useLog();
  const [sendEmailNotification, setSendEmailNotification] = useState<boolean>(true);
  const { authorized: paymentApproverOwn } = useAuthorization([[UserRole.PAYMENTS_APPROVER_OWN]]);
  const [insufficientFunds, setInsufficientFunds] = useState<boolean>(false);
  const [totalSectionData, setTotalSectionData] = useState<TKeyValue[]>([]);

  useEffect(() => {
    setSendEmailNotification(!paymentApproverOwn);
  }, [paymentApproverOwn]);

  useEffect(() => {
    if (
      authyState.type?.type === 'DRAWDOWN_BATCH'
      && authyState.status === 'SUCCESS'
      && !paymentsForm.isSubmitting
      && paymentsForm.values.payments?.length
      && sendEmailNotification
    ) {
      onSubmissionSuccess();
    }
  }, [authyState.status, authyState.type]);

  useEffect(() => {
    checkInsufficientAmount();
  }, [requestForm.values.fixCurrencyCode, requestForm.values.totalDrawdownAmount, requestForm.values.weightedRate]);

  const checkInsufficientAmount = () => {
    const amountValue = parseFloat(requestForm.values.totalDrawdownAmount.toString().replace(/,/g, ''));

    if (requestForm.values.fixCurrencyCode === requestForm.values.sellCurrencyCode) {
      if (defaultSellCurrencyAccount) {
        setInsufficientFunds(defaultSellCurrencyAccount.clearedBalance < amountValue);
      }
    } else if (defaultSellCurrencyAccount) {
      setInsufficientFunds(
        defaultSellCurrencyAccount.clearedBalance < (amountValue / requestForm.values.weightedRate),
      );
    }
  };

  const onSubmissionSuccess = async () => {
    if (paymentsForm.values.payments?.length) {
      try {
        const approvers: Approver[] = await PaymentsService.getApprovers(
          paymentsForm.values.payments[0].id || '',
        );
        const approverIds: string[] = approvers.flatMap((approver: Approver) => (
          approver.eligible ? approver.userId : []));
        PaymentsService.postGenericPaymentApproverEmail({ approverIds });
      } catch (error) {
        sb.trigger(t('there_was_an_error_sending_your_email_notification'), 'error');
        logError({ action: 'Error sending email notification' });
      }
    }
  };

  const showFunding = (): boolean => {
    if (section === 'Drawdown' && requestForm.values.fixCurrencyCode && requestForm.values.valueDate
      && requestForm.values.totalDrawdownAmount && requestForm.values.totalDrawdownAmount > 0 && !requestForm.errors.totalDrawdownAmount) {
      return true;
    }
    return false;
  };

  const onClose = () => {
    setConfirmationModalType('CLOSE');
    setOpenConfirmation(true);
  };

  useEffect(() => {
    const totalDrawdownSection: TKeyValue[] = [
      {
        key: t('amount'),
        value: `${getCurrencySymbol(requestForm.values.fixCurrencyCode)} ${formatNumber(requestForm.values.totalDrawdownAmount, 2, false, true)}`,
        boldValue: true,
      },
      {
        key: t('weighted_rate'),
        value: formatNumber(calculatedWeightedRate || 0, 4, false, true),
        boldValue: true,
      },
    ];
    setTotalSectionData(totalDrawdownSection);
  }, [requestForm?.values?.totalDrawdownAmount, calculatedWeightedRate]);

  const getConfirmButtonText = (type: string): string => {
    if (type === 'CLEAR') return t('clear');
    if (type === 'CLOSE') return t('close');
    if (type === 'BACK_TO_DRAWDOWN') return t('back_to_drawdown');
    return t('remove');
  };

  const getConfirmationContent = (type: string): string => {
    if (type === 'CLOSE') return t('you_will_be_closing_this_form');
    if (type === 'CLEAR') return t('you_will_be_clearing_all_input_from_this_form');
    if (type === 'BACK_TO_DRAWDOWN') return t('cancel_payments_check');
    return t('you_will_be_removing_this_trade');
  };

  return (
    <>
      <BaseDrawer.Drawer
        data-testid="new-mass-drawdown-drawer"
        open={massDrawdownDrawerOpen}
        onClose={onClose}
        anchor="right"
        className={classes.drawerContainer}
        closeIdentifier="new-mass-info-drawer"
      >
        <BaseDrawer.Header
          headerTitle={(
            <>
              <FontAwesomeIcon icon={faArrowDown} className={classes.icon} />
              {t('mass_drawdown')}
            </>
        )}
        />
        <BaseDrawer.Body>
          <div className={classes.drawerBody}>
            {section === 'Drawdown' && (
            <>
              <DrawdownCreation
                defaultSellCurrencyAccount={defaultSellCurrencyAccount!}
                accountConfigQuery={accountConfigQuery}
                selectedTrades={selectedTrades}
                requestForm={requestForm}
                handleSplitChange={handleSplitChange}
                handleCurrencyChange={handleCurrencyChange}
                handleAmountChange={handleAmountChange}
                massDrawdownValueDate={massDrawdownValueDate}
                additionalTrades={additionalTrades}
                handleAddAdditionalTrade={handleAddAdditionalTrade}
                setConfirmationModalType={setConfirmationModalType}
                setOpenConfirmation={setOpenConfirmation}
                setTradeIdToRemove={setTradeIdToRemove}
                insufficientFunds={insufficientFunds}
                showFunding={showFunding}
                tradeErrors={tradeErrors}
              />

              <DrawerBackdropLoader display={validateDrawdownMutations.isLoading} text={t('validating_your_drawdowns')} width="900px" />
            </>
            )}
            {
            section === 'Settlement' && (
              <Settlement
                selectedTrades={selectedTrades}
                handleSetSection={handleSetSection}
                form={requestForm}
                paymentsForm={paymentsForm}
                buyCurrencyAccount={defaultBuyCurrencyAccount}
                sellCurrencyAccount={defaultSellCurrencyAccount}
                validatedDrawdownsResponse={validatedDrawdownsResponse}
                setOpenPaymentClearWarning={setOpenConfirmation}
                setWarningModalConfirmationAction={setConfirmationModalType}
              />
            )
          }
            {
            section === 'Submit' && (
              <>
                <DrawdownConfirmation
                  handleSetSection={handleSetSection}
                  drawdownRequestForm={requestForm.values}
                  validatedDrawdownsResponse={validatedDrawdownsResponse}
                  submissionForm={drawdownSubmissionForm}
                  paymentValues={paymentsForm.values}
                  handleFormSubmit={handleDrawdownMFA}
                  sellCurrencyAccount={defaultSellCurrencyAccount!}
                  buyCurrencyAccount={defaultBuyCurrencyAccount!}
                  sendEmailNotification={sendEmailNotification}
                  setSendEmailNotification={setSendEmailNotification}
                  selectedTrades={selectedTrades}
                />
                <DrawerBackdropLoader
                  display={drawdownSubmissionForm.isSubmitting || submitDrawdownMutations.isLoading}
                  text={t('submitting_your_drawdown')}
                  width="900px"
                />
              </>
            )
          }
          </div>
        </BaseDrawer.Body>
        {section === 'Drawdown' && (
        <div style={{
          width: '100%',
          bottom: 0,
          opacity: 1,
          position: 'sticky',
          padding: '20px',
          background: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.04)',
          boxShadow: '10px 10px 10px rgba(0, 0, 0, 0.04)',
        }}
        >

          {showFunding() && (
          <div style={{ paddingBottom: '20px' }}>
            <div className={classes.detailTable}>
              <StyledKeyValueTable
                testId="drawdown-data-table"
                data={totalSectionData}
              />
            </div>
          </div>
          )}

          <Box className={classes.buttonsContainer}>
            <ActionButton
              size="medium"
              style={section === 'Drawdown' ? {
                background: '#A62B231A',
                color: '#A62B23',
                minWidth: '100px',
              } : {
                background: '#F7F7F7',
                color: '#212529',
                minWidth: '134px',
              }}
              onClick={() => {
                if (section === 'Drawdown') {
                  setOpenConfirmation(true);
                  setConfirmationModalType('CLEAR');
                } else {
                  if (section === 'Settlement') handleSetSection('Drawdown');
                  if (section === 'Submit') handleSetSection('Settlement');
                }
              }}
            >
              {t('clear_all')}
            </ActionButton>
            <ActionButton
              fullWidth
              disabled={!requestForm.isValid
              || (requestForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE && insufficientFunds)
              || !requestForm.values.totalDrawdownAmount
              || Object.keys(tradeErrors).length > 0}
              onClick={() => {
                handleFormSubmit(selectedTrades);
              }}
              size="medium"
              testId="mass-drawdown-continue-to-settlement-btn"
              className="mass-drawdown-continue-to-settlement"
            >
              {t('continue_to_settlment')}
            </ActionButton>
          </Box>

        </div>
        )}

        <ConfirmationModal
          variant="negative"
          bothButtons
          title={(
            <span>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size="lg"
                color="#CF752F"
                className="titleIcon"
              />
              {' '}
              <span style={{ marginLeft: '4px', marginTop: '2px' }}>
                {t('are_you_sure')}
              </span>
            </span>
        )}
          open={openConfirmation}
          onExit={() => {
            setOpenConfirmation(false);
          }}
          onConfirm={handleConfirmationModal}
          exitButtonText={t('cancel')}
          content={(
            <p
              style={{
                fontSize: '13px',
                fontWeight: 400,
                color: '#212529BF',
              }}
            >
              {getConfirmationContent(confirmationModalType)}
            </p>
        )}
          width="600px"
          buttonSpacing="space-between"
          confirmButtonWidth={458}
          cancelButtonWidth={44}
          confirmButtonText={getConfirmButtonText(confirmationModalType)}
        />
      </BaseDrawer.Drawer>
    </>
  );
};

export default NewMassDrawdownDrawer;
